/**
 * Bridges the room's wire protocol to the chess client. The server is
 * **authoritative**: every local action leaves as an intent and every state
 * change arrives as a `STATE_SYNC<ChessSnapshot>`, handed on untouched to
 * `onSnapshot` (the online match rebuilds the game from it).
 *
 * Everything that is not a snapshot is translated here: the connection and
 * rematch states go to `OverlayMachine`, and the seat → colour mapping, the
 * opponent's presence and the restart handshake are tracked as plain fields
 * the scene reads.
 */
import type { ConnectionManager, RoomClient } from '@bn-games/room-client';
import type { SeatedPlayer } from '@bn-games/network-protocol';
import type { Color } from '../game/types.js';
import { RestartHandshake } from './RestartHandshake.js';
import { OverlayMachine, isPausedOverlay, type GameOverResult, type OverlayView } from './overlayState.js';
import { SEATS, colorOfSeat, type ChessSnapshot, type ClientMessage, type ServerMessage } from './protocol.js';

export interface NetworkGameControllerOptions {
  readonly room: RoomClient<ClientMessage, ServerMessage>;
  readonly onSnapshot: (snapshot: ChessSnapshot) => void;
  /** Presence, colours or the restart handshake changed — repaint. */
  readonly onChange: () => void;
  /** The opponent nudged us (`OPPONENT_PINGED`). */
  readonly onPinged: () => void;
  /** The opponent proposed (true) or withdrew/settled (false) a restart. */
  readonly onRestartProposal: (open: boolean) => void;
}

export class NetworkGameController {
  private readonly connection: ConnectionManager<ClientMessage, ServerMessage>;
  private readonly machine: OverlayMachine;
  private overlayListener: ((view: OverlayView) => void) | null = null;
  private unsubMessage: (() => void) | null = null;
  private unsubState: (() => void) | null = null;
  /** Seat roles in slot order; a rematch rotates them, and `RoomClient` does not track that. */
  private seats: readonly string[] | null;
  private away = false;
  private readonly restart = new RestartHandshake();

  constructor(private readonly opts: NetworkGameControllerOptions) {
    this.connection = opts.room.getConnection();
    this.seats = opts.room.getInfo().seats;
    this.machine = new OverlayMachine(() => this.overlayListener?.(this.machine.view()));
  }

  /** The colour the server gave us, or null before a seat is known. */
  get localColor(): Color | null {
    const seat = this.opts.room.getInfo().localSeat;
    return seat === null ? null : colorOfSeat((this.seats ?? SEATS)[seat]);
  }

  /** True while the opponent's socket is down. Their clock keeps running. */
  get opponentAway(): boolean {
    return this.away;
  }

  /** False while our own link is down: no intent may leave. */
  get playable(): boolean {
    return !isPausedOverlay(this.machine.view().overlay);
  }

  /** The other seat's roster entry, if the room has one. */
  get opponent(): SeatedPlayer | null {
    const info = this.opts.room.getInfo();
    return info.players?.find((p) => p.seat !== info.localSeat) ?? null;
  }

  attach(): void {
    if (this.unsubMessage !== null) return;
    this.unsubMessage = this.connection.onMessage((msg) => this.handleServerMessage(msg));
    this.unsubState = this.connection.onStateChange((state) => {
      if (state === 'disconnected') this.machine.selfDisconnected(Date.now());
      else if (state === 'connected') this.machine.selfConnected();
    });
  }

  detach(): void {
    this.unsubMessage?.();
    this.unsubMessage = null;
    this.unsubState?.();
    this.unsubState = null;
    this.overlayListener = null;
    this.machine.dispose();
  }

  // --- Overlay ---

  /** Subscribe to overlay changes. Called back immediately with the current view. */
  onOverlayChange(listener: (view: OverlayView) => void): void {
    this.overlayListener = listener;
    listener(this.machine.view());
  }

  showGameOver(result: GameOverResult): void {
    this.machine.gameOver(result);
  }

  requestRematch(): void {
    this.connection.send({ type: 'REMATCH_REQUEST' });
    this.machine.rematchOffered();
  }

  declineRematch(): void {
    this.connection.send({ type: 'REMATCH_DECLINE' });
    this.dismissOverlay();
  }

  /** Step back from a rematch state: to the result if the game is over, else to the board. */
  dismissOverlay(): void {
    const result = this.machine.view().gameOverResult;
    if (result === null) this.machine.dismiss();
    else this.machine.gameOver(result);
  }

  /** Send an intent. The server answers with a snapshot; nothing is assumed here. */
  send(message: ClientMessage): void {
    this.connection.send(message);
  }

  // --- Per-game state ---

  restartState = (): { pending: boolean; canPropose: boolean } => this.restart.state();

  requestRestart(): void {
    if (!this.restart.propose()) return;
    this.connection.send({ type: 'RESTART_REQUEST' });
    this.opts.onChange();
  }

  /**
   * A fresh game: a clean restart budget, no result or rematch overlay. Also called when
   * the snapshots show one — a `REMATCH_START` sent while our socket was down is never replayed.
   */
  newGame(): void {
    this.restart.reset();
    this.opts.onRestartProposal(false);
    this.machine.gameStarted();
  }

  /** The opponent just moved, so they are there — whatever we last heard while reconnecting. */
  opponentSeen(): void {
    this.away = false;
  }

  // --- Internals ---

  private handleServerMessage(msg: ServerMessage): void {
    switch (msg.type) {
      case 'STATE_SYNC':
        this.opts.onSnapshot(msg.snapshot);
        return;
      case 'GAME_START':
      case 'REMATCH_START':
      case 'RESTART_START':
        if (msg.seats !== undefined) this.seats = msg.seats;
        this.away = false;
        this.newGame();
        break;
      case 'RECONNECT_OK':
        if (msg.seats !== undefined) this.seats = msg.seats;
        this.machine.reconnected();
        break;
      case 'RECONNECT_FAIL':
        this.machine.disconnected();
        break;
      case 'OPPONENT_DISCONNECTED':
        this.away = true;
        break;
      case 'OPPONENT_RECONNECTED':
        this.away = false;
        break;
      case 'REMATCH_REQUEST':
        this.machine.rematchRequested();
        break;
      case 'REMATCH_DECLINE':
        this.machine.rematchDeclined();
        break;
      case 'RESTART_PENDING':
        this.restart.opened();
        if (msg.requestedBy !== this.opts.room.getInfo().localSeat) this.opts.onRestartProposal(true);
        break;
      case 'RESTART_CANCELLED':
        this.restart.cancelled();
        this.opts.onRestartProposal(false);
        break;
      case 'RESTART_REJECTED':
        this.restart.rejected(msg.reason);
        break;
      case 'OPPONENT_PINGED':
        this.opts.onPinged();
        return;
      default:
        // Lobby traffic — `RoomClient` owns it.
        return;
    }
    this.opts.onChange();
  }
}
